import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MathBlock, MathInline } from "../components/MathRender";
import { StatusBadge, VerdictLabel } from "../components/StatusBadge";
import { ArrowLeft, Award, Clock, ShieldCheck, FileText, Banknote } from "lucide-react";

function SubmissionComposer({ problemId, onSubmitted }) {
  const [content, setContent] = useState("\\begin{aligned}\n  \n\\end{aligned}");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/solve/problems/${problemId}/submit`, { content_latex: content, notes });
      toast.success("Submission received — now in independent review");
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="axiom-card p-6" data-testid="submission-composer">
      <h3 className="font-display text-lg text-slate-900">Compose your submission</h3>
      <p className="mt-1 text-sm text-slate-500">Write LaTeX on the left; the rendered proof previews live on the right.</p>
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <label className="eyebrow">LaTeX source</label>
          <textarea data-testid="submission-latex" value={content} onChange={(e) => setContent(e.target.value)} rows={12}
            className="mt-2 w-full rounded-lg border border-[#CBD5E1] bg-slate-50 px-3 py-3 font-mono text-sm text-[#B45309] outline-none focus:border-[#E61A27]" />
        </div>
        <div>
          <label className="eyebrow">Live preview</label>
          <div className="mt-2 min-h-[12rem] rounded-lg border border-[#CBD5E1] bg-slate-50 p-4" data-testid="submission-preview">
            <MathBlock>{content}</MathBlock>
          </div>
        </div>
      </div>
      <label className="eyebrow mt-4 block">Notes for reviewers (optional)</label>
      <textarea data-testid="submission-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
        className="mt-2 w-full rounded-lg border border-[#CBD5E1] bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-[#E61A27]" />
      <button data-testid="submission-submit-btn" onClick={submit} disabled={loading} className="btn-crimson mt-4">
        {loading ? "Submitting…" : "Submit for review"}
      </button>
    </div>
  );
}

export default function ProblemDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [subs, setSubs] = useState([]);

  const load = useCallback(() => {
    api.get(`/solve/problems/${id}`).then((r) => setP(r.data)).catch(() => {});
    if (user && (user.role === "customer" || user.role === "reviewer" || user.role === "adjudicator" || user.role === "admin")) {
      api.get(`/solve/problems/${id}/submissions`).then((r) => setSubs(r.data)).catch(() => {});
    }
  }, [id, user]);

  useEffect(() => { load(); }, [load]);

  if (!p) return <div className="px-6 py-20 text-center text-slate-500">Loading…</div>;

  const isOwner = user && p.customer_id === user.id;
  const canFund = isOwner && p.status === "draft";
  const canSubmit = user?.role === "contributor" && ["open", "in_review"].includes(p.status);

  const fund = async () => {
    try { await api.post(`/solve/problems/${id}/fund`); toast.success("Escrow funded (simulated) — commission is now open"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not fund"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="problem-detail-page">
      <Link to="/solve" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to marketplace
      </Link>

      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full border border-[#CBD5E1] px-2.5 py-0.5 text-[11px] font-mono text-[#D4AF37]">{p.domain}</span>
        <span className="text-[11px] font-mono text-slate-500">{p.difficulty}</span>
        <StatusBadge status={p.status} />
        {!p.allow_ai && <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-[11px] font-mono text-rose-700">No AI assistance</span>}
      </div>

      <h1 className="mt-3 font-display text-3xl tracking-tight text-slate-900 sm:text-4xl">{p.title}</h1>
      <div className="mt-5"><MathBlock>{p.statement_latex}</MathBlock></div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="axiom-card p-4"><p className="eyebrow">Award</p><p className="mt-1 flex items-center gap-2 text-lg text-emerald-700"><Award className="h-5 w-5" />${p.prize_amount?.toLocaleString()}</p></div>
        <div className="axiom-card p-4"><p className="eyebrow">Review budget</p><p className="mt-1 flex items-center gap-2 text-lg text-cyan-700"><ShieldCheck className="h-5 w-5" />${p.review_budget?.toLocaleString()}</p></div>
        <div className="axiom-card p-4"><p className="eyebrow">Deadline</p><p className="mt-1 flex items-center gap-2 text-lg text-slate-700"><Clock className="h-5 w-5" />{p.deadline ? new Date(p.deadline).toLocaleDateString() : "—"}</p></div>
      </div>

      <div className="axiom-card mt-6 p-6">
        <h3 className="font-display text-lg text-[#B45309]">Acceptance criteria</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.acceptance_criteria}</p>
        <h3 className="mt-5 font-display text-lg text-[#B45309]">IP &amp; rights</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.ip_terms}</p>
        <p className="mt-2 text-xs text-slate-500">Tie rule: {p.tie_rule} · Awards: {p.award_count} · Platform fee applies to accepted payout.</p>
      </div>

      {canFund && (
        <div className="axiom-card mt-6 border-[#D4AF37]/30 p-6" data-testid="fund-panel">
          <h3 className="font-display text-lg text-slate-900">This commission is a draft</h3>
          <p className="mt-1 text-sm text-slate-500">Fund the escrow (prize + review budget) to open it for qualified contributors. Simulated — no real money moves.</p>
          <button data-testid="fund-btn" onClick={fund} className="btn-crimson mt-4"><Banknote className="h-4 w-4" /> Fund escrow (simulated)</button>
        </div>
      )}

      {canSubmit && <div className="mt-6"><SubmissionComposer problemId={id} onSubmitted={load} /></div>}

      {(isOwner || ["reviewer", "adjudicator", "admin"].includes(user?.role)) && (
        <div className="mt-8">
          <p className="eyebrow mb-3">Submissions ({subs.length})</p>
          <div className="space-y-3">
            {subs.map((s) => (
              <Link to={`/submissions/${s.id}`} key={s.id} data-testid={`submission-row-${s.id}`} className="axiom-card flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-[#E61A27]" />
                  <div>
                    <p className="text-sm text-slate-900">{s.contributor_name} · <span className="font-mono text-slate-500">v{s.version}</span></p>
                    <p className="text-xs text-slate-500">{s.reviews?.length || 0} review(s)</p>
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </Link>
            ))}
            {subs.length === 0 && <p className="text-sm text-slate-500">No submissions yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
